import { useCallback, useEffect, useState } from 'react';

import { useRouter } from 'expo-router';
import { isArray } from 'lodash-es';

import { MenuObjectTypes } from 'enums/menu';
import { selectors, useAppSelector } from 'state/global-state';
import { IncentiveEvaluationErrorCodes } from 'state/loyalty/hooks/types';
import { useGetMenuObjectLazyQuery } from 'state/menu/menu-options/hooks/use-get-menu-object-lazy-query';
import { IMenuObject } from 'state/menu/types';
import logger from 'utils/logger';

type UnsatisfiedCartRequirementsAdditionalInfo = {
  id: string;
  name: string;
  quantity: number;
  path: string;
  onPress(): void;
};

export type OfferFeedbackAdditionalInfo = {
  [IncentiveEvaluationErrorCodes.UNSATISFIED_CART_REQUIREMENTS]?: UnsatisfiedCartRequirementsAdditionalInfo[][];
};

type UseFeedbackAdditionalInfo = {
  loading: boolean;
  additionalInfo: OfferFeedbackAdditionalInfo;
};

/**
 * Hook to fetch and process additional information for loyalty offer feedback.
 *
 * This hook handles feedback for specific incentive evaluation error codes. Currently,
 * it supports `UNSATISFIED_CART_REQUIREMENTS`, where users need to add specific items
 * to their cart to meet offer requirements. The hook is designed to be extensible, so
 * other types of feedback can be added in the future.
 *
 * The hook:
 * - Retrieves feedback for the given loyalty offer.
 * - Fetches additional details about menu objects (e.g., items) required to satisfy feedback.
 * - Returns structured additional information for display in the UI.
 *
 * @param {Object} params - Parameters for the hook.
 * @param {string} params.loyaltyOfferId - The ID of the loyalty offer for which feedback should be processed.
 *
 * @returns {Object} - The hook's return values.
 * @returns {boolean} return.loading - Indicates whether the hook is currently fetching and processing data.
 * @returns {OfferFeedbackAdditionalInfo} return.additionalInfo - Contains processed feedback additional info.
 *
 **/
export const useFeedbackAdditionalInfo = ({
  loyaltyOfferId,
}: {
  loyaltyOfferId: string;
}): UseFeedbackAdditionalInfo => {
  const getMenuObjectLazyQuery = useGetMenuObjectLazyQuery();
  const { navigate } = useRouter();
  const offersFeedbackMap = useAppSelector(selectors.loyalty.selectOfferFeedbackMap);
  const offerFeedback = offersFeedbackMap[loyaltyOfferId];

  const [unsatisfiedCartRequirementsInfo, setUnsatisfiedCartRequirementsInfo] = useState<
    UnsatisfiedCartRequirementsAdditionalInfo[][] | undefined
  >();
  const [loadingUnsatisfiedCartRequirements, setLoadingUnsatisfiedCartRequirements] = useState(
    false
  );

  // Fetch detailed information for menu objects by their IDs
  const fetchMenuObjects = useCallback(
    async (itemIds: string[]) => {
      const menuObjects: IMenuObject[] = [];
      for (const id of itemIds) {
        try {
          const item = await getMenuObjectLazyQuery(`${MenuObjectTypes.ITEM}-${id}`);
          if (item) {
            menuObjects.push(item);
          }
        } catch (error) {
          logger.error({ error, message: `Failed to fetch menu object with ID ${id}` });
        }
      }
      return menuObjects;
    },
    [getMenuObjectLazyQuery]
  );

  // Process UNSATISFIED_CART_REQUIREMENTS feedback
  const getUnsatisfiedCartRequirementsInfo = useCallback(async () => {
    const { targetValue, currentValue } =
      offerFeedback?.find(
        ({ code }) => code === IncentiveEvaluationErrorCodes.UNSATISFIED_CART_REQUIREMENTS
      ) || {};

    // Collect all menu item IDs from the feedback
    const menuItemIds: string[] =
      (isArray(targetValue) &&
        targetValue?.flatMap((subArray: { id: string; quantity: number }[]) =>
          subArray
            .flatMap(({ id, quantity }) =>
              quantity && quantity > 1 ? Array.from({ length: quantity }, () => id) : id
            )
            .filter(Boolean)
        )) ||
      [];

    if (!menuItemIds.length) {
      setUnsatisfiedCartRequirementsInfo(undefined);
      return;
    }

    setLoadingUnsatisfiedCartRequirements(true);

    // Fetch detailed data for the unique menu item IDs
    const menuObjectsData = await fetchMenuObjects(Array.from(new Set(menuItemIds)));

    // Calculate the quantity of items still needed to satisfy the cart requirements
    const menuObjectQuantities: Record<string, number>[] = targetValue.map(
      (rule: { id: string; quantity: number }[]) => {
        return rule.reduce((acc, cur) => {
          if (acc[cur.id]) {
            return { ...acc, [cur.id]: acc[cur.id] + cur.quantity };
          }
          return { ...acc, [cur.id]: cur.quantity };
        }, {} as Record<string, number>);
      }
    );

    // Create a structured list of items with additional info (name, path, quantity, etc.)
    const data: UnsatisfiedCartRequirementsAdditionalInfo[][] = menuObjectQuantities
      .map(rule => {
        return Object.entries(rule).reduce((acc, [id, qty]) => {
          const currentQuantity: number = currentValue?.[id] || 0;
          const quantity = qty - currentQuantity;

          // Find detailed menu object info for each ID
          const menuObject = menuObjectsData.find(({ _id }) => _id === id);
          if (!menuObject) {
            return acc;
          }
          const { name, _type } = menuObject;
          const productLink = `/menu/${_type?.toLowerCase()}-${id}`;

          return [
            ...acc,
            {
              id,
              name: name?.locale?.trim() || '',
              path: productLink,
              quantity: quantity > 0 ? quantity : 0,
              onPress: () => navigate(productLink), // Navigation handler for the item
            },
          ];
        }, [] as UnsatisfiedCartRequirementsAdditionalInfo[]);
      })
      // Filter out items with a quantity of 0.
      // This should never occur, as having even one item with a quantity of 0 implies that
      // the requirement was met. Hence, the feedback will not be included in the offerFeedback array.
      .filter(obj => !obj.some(({ quantity }) => quantity === 0));

    setUnsatisfiedCartRequirementsInfo(data);
    setLoadingUnsatisfiedCartRequirements(false);
  }, [fetchMenuObjects, navigate, offerFeedback]);

  // Automatically fetch and process feedback when offerFeedback changes
  useEffect(() => {
    getUnsatisfiedCartRequirementsInfo();
  }, [getUnsatisfiedCartRequirementsInfo]);

  return {
    loading: loadingUnsatisfiedCartRequirements,
    additionalInfo: {
      [IncentiveEvaluationErrorCodes.UNSATISFIED_CART_REQUIREMENTS]: unsatisfiedCartRequirementsInfo,
    },
  };
};
